/**
 * useSieMasterData Hook
 * 
 * Shared React Query hook for SIE master data.
 * Provides automatic synchronization between components when data changes.
 * 
 * Usage:
 * - Use useSieMasterData(auditId) to fetch data
 * - Use useInvalidateSieMasterData() to trigger refetch after uploads
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  SIEProcessedData, 
  IncomeStatementData, 
  MonthlyIncomeData, 
  BalanceSheetData,
  SIEVoucher,
  SIEAccount,
  SIEDataQuality,
} from '@/components/sie/types';

// Query key factory for consistent cache keys
export const sieMasterDataKeys = {
  all: ['sie-master-data'] as const,
  byAudit: (auditId: string) => [...sieMasterDataKeys.all, auditId] as const,
};

interface SieMasterDataResult {
  currentYear: SIEProcessedData | null;
  previousYear: SIEProcessedData | null;
  classificationSchemaName: string | null;
  mergeMetadata: {
    totalFiles: number;
    sourceFiles: Array<{ filename?: string; voucherSeries: string[]; voucherCount: number }>;
    duplicatesSkipped: number;
  } | null;
}

/**
 * Reconstructs SIEProcessedData from a database row
 */
function reconstructSieData(row: any): SIEProcessedData {
  return {
    metadata: {
      program: '',
      programVersion: '',
      generated: '',
      companyName: row.company_name || '',
      orgNumber: row.org_number || '',
      fiscalYears: row.fiscal_year_start && row.fiscal_year_end 
        ? [{ index: 0, start: row.fiscal_year_start, end: row.fiscal_year_end }]
        : [],
      currency: 'SEK',
      sieType: '4',
    },
    aggregated: {
      incomeStatement: (row.income_statement as unknown as IncomeStatementData) || {
        revenue: [],
        expenses: [],
        financialIncome: [],
        financialExpenses: [],
        appropriations: [],
        taxes: [],
        totalRevenue: 0,
        totalExpenses: 0,
        operatingResult: 0,
        totalFinancialIncome: 0,
        totalFinancialExpenses: 0,
        financialNet: 0,
        resultAfterFinancial: 0,
        totalAppropriations: 0,
        totalTaxes: 0,
        netResult: 0,
      },
      incomeStatementByMonth: (row.income_statement_by_month as unknown as Record<string, MonthlyIncomeData>) || {},
      incomeStatementByAccountAndMonth: (row.income_statement_by_account_and_month as Record<string, Record<string, number>>) || {},
      balanceSheet: (row.balance_sheet as unknown as BalanceSheetData) || {
        assets: [],
        liabilities: [],
        equity: [],
        totalAssets: 0,
        totalLiabilities: 0,
        totalEquity: 0,
      },
      statistics: {
        totalAccounts: row.total_accounts || 0,
        totalVouchers: row.total_vouchers || 0,
        totalTransactions: row.total_transactions || 0,
        vouchersByMonth: (row.voucher_summary as any)?.byMonth || {},
        vouchersBySeries: (row.voucher_summary as any)?.bySeries || {},
      },
      // Parse dataQuality from stored data if available
      dataQuality: (row.data_quality as unknown as SIEDataQuality) || undefined,
    },
    parseErrors: [],
    vouchers: (row.vouchers as unknown as SIEVoucher[]) || [],
    accounts: (row.accounts as unknown as SIEAccount[]) || [],
  };
}

/**
 * Fetches and transforms SIE master data for an audit
 */
async function fetchSieMasterData(auditId: string): Promise<SieMasterDataResult> {
  const { data, error } = await supabase
    .from('sie_master_data')
    .select('*')
    .eq('audit_id', auditId)
    .order('fiscal_year_index', { ascending: false });

  if (error) throw error;

  let currentYear: SIEProcessedData | null = null;
  let previousYear: SIEProcessedData | null = null;
  let classificationSchemaName: string | null = null;
  let mergeMetadata: SieMasterDataResult['mergeMetadata'] = null;

  if (data && data.length > 0) {
    for (const row of data) {
      const fiscalYearIndex = row.fiscal_year_index ?? 0;
      const reconstructed = reconstructSieData(row);

      if (fiscalYearIndex === 0) {
        currentYear = reconstructed;
        classificationSchemaName = row.classification_schema_name || null;
        
        // Extract merge metadata if present
        if (row.merge_metadata) {
          const meta = row.merge_metadata as any;
          mergeMetadata = {
            totalFiles: meta.totalFiles || 0,
            sourceFiles: meta.sourceFiles || [],
            duplicatesSkipped: meta.statistics?.duplicateVouchersSkipped || 0,
          };
        }
      } else if (fiscalYearIndex === -1) {
        previousYear = reconstructed;
      }
    }
  }

  return { currentYear, previousYear, classificationSchemaName, mergeMetadata };
}

/**
 * React Query hook for SIE master data
 * 
 * @param auditId - The audit ID to fetch data for
 * @returns Query result with currentYear, previousYear, and metadata
 */
export function useSieMasterData(auditId: string | undefined) {
  return useQuery({
    queryKey: sieMasterDataKeys.byAudit(auditId!),
    queryFn: () => fetchSieMasterData(auditId!),
    enabled: !!auditId,
    staleTime: 5 * 60 * 1000, // 5 minutes - SIE data rarely changes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to invalidate SIE master data cache
 * 
 * Call this after uploading/updating SIE files to trigger
 * automatic refetch in all components using useSieMasterData
 * 
 * @returns Function that invalidates cache for a specific audit
 */
export function useInvalidateSieMasterData() {
  const queryClient = useQueryClient();
  
  return (auditId: string) => {
    console.log('[useSieMasterData] Invalidating cache for audit:', auditId);
    queryClient.invalidateQueries({ 
      queryKey: sieMasterDataKeys.byAudit(auditId) 
    });
  };
}

/**
 * Hook to check if SIE data exists for an audit (lightweight check)
 * Uses the same cache as useSieMasterData for consistency
 */
export function useSieMasterDataStatus(auditId: string | undefined) {
  const { data, isLoading, error } = useSieMasterData(auditId);
  
  return {
    hasData: !!data?.currentYear,
    companyName: data?.currentYear?.metadata.companyName || null,
    isLoading,
    error,
  };
}
